/**
 * The addresses a document's `w:hyperlink` elements point to, by relationship id.
 *
 * A `w:hyperlink` element carries no address itself: its `r:id` names a
 * relationship in the main document's relationships part, whose `Target` is the
 * URL. The striker needs the URL to write a link it strikes whole as a HYPERLINK
 * field, the one spelling of a link a tracked deletion can hold whole, so the
 * package's relationships are read once per build and handed down with each
 * paragraph's redline.
 *
 * EXAMPLE
 *   <Relationship Id="rId11" Type="…/relationships/hyperlink" Target="https://x.example/fees" TargetMode="External"/>
 *   -> Map { "rId11" => "https://x.example/fees" }
 */
import { flatOpcPartXml, parseXmlDocumentOrNull } from "./flat-opc-parts";
import { DOCUMENT_RELATIONSHIPS_PART_NAME, HYPERLINK_RELATIONSHIP_TYPE, linkAddressOf, OFFICE_RELATIONSHIPS_NS, PACKAGE_RELATIONSHIPS_NS, WORDPROCESSINGML_NS } from "./ooxml-names";

/** A hyperlink relationship's `Target`, by its `Id`. */
export type HyperlinkTargetsByRelationshipId = ReadonlyMap<string, string>;

/** No address known for any link: the striker strikes an element-form link's runs inside it, as before. */
export const NO_HYPERLINK_TARGETS: HyperlinkTargetsByRelationshipId = new Map();

/**
 * Every hyperlink relationship of the package's main document.
 *
 * CASES
 *   - the package has the main document's relationships part -> its hyperlink relationships, by id
 *   - a bare fragment, or a package without that part         -> no targets
 *   - a relationship of another type (styles, images, …)      -> left out
 *   - a hyperlink relationship missing its `Id` or `Target`   -> left out: nothing to look up or to write
 */
export function hyperlinkTargetsOf(packageDocument: Document): HyperlinkTargetsByRelationshipId {
    const relationshipsXml = flatOpcPartXml(packageDocument, DOCUMENT_RELATIONSHIPS_PART_NAME);
    /** A fragment, or a package that relates nothing to its document. */
    if (relationshipsXml === null) return NO_HYPERLINK_TARGETS;
    const relationshipsDocument = parseXmlDocumentOrNull(relationshipsXml);
    /** A relationships part that does not parse names no address. */
    if (relationshipsDocument === null) return NO_HYPERLINK_TARGETS;
    const targetsByRelationshipId = new Map<string, string>();
    for (const relationship of Array.from(relationshipsDocument.getElementsByTagNameNS(PACKAGE_RELATIONSHIPS_NS, "Relationship"))) {
        if (relationship.getAttribute("Type") !== HYPERLINK_RELATIONSHIP_TYPE) continue;
        const relationshipId = relationship.getAttribute("Id");
        const target = relationship.getAttribute("Target");
        if (!relationshipId || !target) continue;
        targetsByRelationshipId.set(relationshipId, target);
    }
    return targetsByRelationshipId;
}

/**
 * The attributes of a `w:hyperlink` element that say where it points and how it
 * opens, each null when the element does not carry it.
 */
export interface HyperlinkElementAttributes {
    /** `r:id`: the relationship whose `Target` is the address. */
    relationshipId: string | null;
    /** `w:anchor`: a place inside the target, or, with no `r:id`, a bookmark of this document. */
    placeInTarget: string | null;
    /** `w:tooltip`: the tip Word shows over the link. */
    tooltip: string | null;
    /** `w:tgtFrame`: the window or frame the link opens in ("_blank"). */
    targetFrame: string | null;
}

/** The one reading of a `w:hyperlink` element's attributes, for the striker that rewrites it and the reader that reports its address. */
export function hyperlinkElementAttributesOf(hyperlinkElement: Element): HyperlinkElementAttributes {
    return {
        relationshipId: attributeOf(hyperlinkElement, OFFICE_RELATIONSHIPS_NS, "id", "r:id"),
        placeInTarget: attributeOf(hyperlinkElement, WORDPROCESSINGML_NS, "anchor", "w:anchor"),
        tooltip: attributeOf(hyperlinkElement, WORDPROCESSINGML_NS, "tooltip", "w:tooltip"),
        targetFrame: attributeOf(hyperlinkElement, WORDPROCESSINGML_NS, "tgtFrame", "w:tgtFrame"),
    };
}

/**
 * The address a `w:hyperlink` element points to, as `linkAddressOf` spells it.
 *
 * CASES
 *   - its `r:id` names a hyperlink relationship of the package -> that target, with "#" and its
 *                                                                 `w:anchor` where it has one
 *   - its `r:id` names none the package holds                  -> null: the address is not known
 *   - no `r:id`, a `w:anchor`                                  -> "#" and the anchor: a bookmark of
 *                                                                 this document
 *   - neither                                                  -> null
 *
 * EXAMPLE
 *   r:id "rId11" -> "https://x.example/fee"                -> "https://x.example/fee"
 *   w:anchor "Schedule_1"                                   -> "#Schedule_1"
 *   r:id "rId99", not in the package                        -> null
 */
export function hyperlinkElementAddressOf(hyperlinkElement: Element, hyperlinkTargets: HyperlinkTargetsByRelationshipId): string | null {
    const { relationshipId, placeInTarget } = hyperlinkElementAttributesOf(hyperlinkElement);
    /** WHEN the element names no relationship CONSEQUENCE it points at a bookmark of this document, or nowhere. */
    if (relationshipId === null) return linkAddressOf(null, placeInTarget);
    const target = hyperlinkTargets.get(relationshipId);
    /** NULL: a relationship the package does not hold, or a paragraph read without its package. */
    if (target === undefined) return null;
    return linkAddressOf(target, placeInTarget);
}

/**
 * An attribute's value, read by namespace or, where the parser kept only the
 * prefixed name, by that name.
 *
 * NULL
 *   The attribute is absent or empty: there is nothing to read for it. The browser
 *   answers an absent attribute "" and xmldom null; both are null here.
 */
function attributeOf(element: Element, namespace: string, localName: string, prefixedName: string): string | null {
    const value = element.getAttributeNS(namespace, localName) || element.getAttribute(prefixedName);
    /** EMPTY value: "" is read as absent. */
    if (!value) return null;
    return value;
}
